import { Add, Delete, Edit } from '@mui/icons-material';
import { Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useMemo, useState } from 'react';
import {
  Button as SMButton,
  Page,
  PageTitleWithButton,
  Pagination,
  Table,
  Toast
} from '../../../components';
import { deleteCounty, getCounties } from '../../../fetch';
import { ICounty } from '../../../models/counties';
import { ManageCountiesModal } from './ManageCountiesModal';
import { MobileCounties } from './MobileCounties';

export const ManageCounties = () => {
  const classes = useStyles();

  const [counties, setCounties] = useState<ICounty[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingCounties, setIsLoadingCounties] = useState(true);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isError, showError] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const fetchCounties = async () => {
    try {
      setIsLoadingCounties(true);
      const res = await getCounties({
        page: page + 1,
        perPage,
        sortBy: 'Name'
      });
      setRecordCount(res.totalRecordCount);
      setCounties(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCounties(false);
    }
  };

  useEffect(() => {
    fetchCounties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleEditCounty = county => {
    setSelectedCounty(county);
    setIsModalOpen(true);
  };

  const handleAddCounty = () => {
    setSelectedCounty(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async (state, name) => {
    const result = window.confirm('Are you sure you want to delete this County?');
    if (result) {
      setDeleting(true);
      try {
        await deleteCounty(state, name);
        fetchCounties();
        setSuccessMessage('County Deleted!');
      } catch (error) {
        let potentialErrorMessage = error?.response?.data?.Detail;
        if (potentialErrorMessage) {
          setErrorMessage(potentialErrorMessage);
        } else {
          setErrorMessage('Unable to delete County.');
        }
        showError(true);
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'County Name',
        accessor: 'name',

        sort: false
      },
      {
        Header: 'State',
        accessor: 'state',

        sort: false
      },
      {
        Header: 'Tax Rate',
        accessor: 'taxRate',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return original.taxRate.toFixed(2);
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <SMButton
                id="edit-county"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleEditCounty(original)}
              >
                Edit
              </SMButton>
              <SMButton
                id="delete"
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original.state, original.name);
                }}
              >
                Delete
              </SMButton>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counties]);

  return (
    <>
      <Page title="Manage Counties">
        <PageTitleWithButton
          pageTitleBackProps={{
            showDivider: false,
            backText: 'Back to Admin home',
            backUrl: '/admin',
            title: 'Manage Counties'
          }}
          button={
            <SMButton
              id="create-county"
              startIcon={<Add />}
              onClick={() => handleAddCounty()}
              color="primary"
              variant="contained"
              className={classes.addRoleButton}
            >
              Add County
            </SMButton>
          }
        />
        <Table
          columns={columns}
          data={counties}
          hidePagination
          isLoading={isLoadingCounties || isDeleting}
          mobileProps={{
            handleEdit: (county: ICounty) => handleEditCounty(county),
            handleDelete: (county: ICounty) => handleDelete(county.state, county.name)
          }}
          ResponsiveComponent={isMobile ? MobileCounties : undefined}
        />
        {!isLoadingCounties && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <ManageCountiesModal
          open={isModalOpen}
          onClose={() => handleModalClose()}
          county={selectedCounty}
          fetchCounties={fetchCounties}
        />
        <Toast
          id="attorney-offices-error"
          autoHideDuration={5000}
          message={errorMessage}
          open={isError}
          onClose={() => showError(false)}
          variant="error"
        />
        <Toast
          id="attorney-offices-success"
          message={successMessage}
          open={!!successMessage}
          onClose={() => setSuccessMessage('')}
          variant="success"
        />
      </Page>
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  actionButtons: {
    textAlign: 'right'
  },
  addRoleButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  addRoleWrapper: {
    marginBottom: theme.spacing(1)
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
